import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "contexts/AuthContext";
import { I18nProvider } from "i18n";
import AppLayout from "components/layout/AppLayout";
import Login from "pages/Login";
import OidcCallback from "pages/OidcCallback";
import VerifyPage from "pages/VerifyPage";
import OidcConsent from "pages/OidcConsent";
import Companies from "pages/admin/Companies";
import CompanyForm from "pages/admin/CompanyForm";
import Accounts from "pages/admin/Accounts";
import AccountForm from "pages/admin/AccountForm";
import AdminSettings from "pages/admin/Settings";
import AISettingsPage from "pages/admin/AI";
import ClientApps from "pages/admin/ClientApps";
import ClientAppForm from "pages/admin/ClientAppForm";
import ApiTokens from "pages/admin/ApiTokens";
import ApiTokenForm from "pages/admin/ApiTokenForm";
import GlobalConfig from "pages/admin/GlobalConfig";
import Sessions from "pages/Sessions";
import SearchResults from "pages/SearchResults";
import Profile from "pages/Profile";
import PortalLogin from "pages/pub/PortalLogin";
import PortalRegister from "pages/pub/PortalRegister";
import PortalEnroll from "pages/pub/PortalEnroll";
import PortalHome from "pages/pub/PortalHome";
import SessionDetail from "pages/SessionDetail";
import SessionDebug from "pages/SessionDebug";
import Jobs from "pages/Jobs";
import People from "pages/admin/People";
import Biometrics from "pages/admin/Biometrics";
import Alerts from "pages/admin/Alerts";
import BiometricPersonDetail from "pages/admin/BiometricPersonDetail";
import "./App.css";

function App() {
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  return (
    <div className="App">
      <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public client portal — its own tree, its own session (lib/portalApi). */}
            <Route path="/pub/login" element={<PortalLogin />} />
            <Route path="/pub/register" element={<PortalRegister />} />
            <Route path="/pub/enroll" element={<PortalEnroll />} />
            <Route path="/pub" element={<PortalHome />} />

            <Route path="/liveness/login" element={<Login />} />
            <Route path="/liveness/sso/callback" element={<OidcCallback />} />

            {/* PUBLIC hosted verification page — authorized by the session token in
                the URL (OAuth-style redirect target), so it is NOT login-guarded. */}
            <Route path="/v/:token" element={<VerifyPage darkMode={darkMode} />} />
            <Route path="/liveness/oidc/continue" element={<OidcConsent />} />

            {/* Administration — full layout with sidebar (login required). */}
            <Route element={<AppLayout darkMode={darkMode} setDarkMode={setDarkMode} />}>
              <Route path="/liveness" element={<Navigate to="/liveness/sessions" replace />} />
              <Route path="/liveness/search" element={<SearchResults />} />
              <Route path="/liveness/profile" element={<Profile />} />
              <Route path="/liveness/sessions" element={<Sessions />} />
              <Route path="/liveness/sessions/:id" element={<SessionDetail />} />
              <Route path="/liveness/sessions/:id/debug" element={<SessionDebug />} />
              <Route path="/liveness/admin/people" element={<People />} />
              <Route path="/liveness/admin/biometrics" element={<Biometrics />} />
              <Route path="/liveness/admin/biometrics/:id" element={<BiometricPersonDetail />} />
              <Route path="/liveness/admin/alerts" element={<Alerts />} />
              <Route path="/liveness/admin/client-apps" element={<ClientApps />} />
              <Route path="/liveness/admin/client-apps/new" element={<ClientAppForm />} />
              <Route path="/liveness/admin/client-apps/:id/edit" element={<ClientAppForm />} />
              <Route path="/liveness/admin/api-tokens" element={<ApiTokens />} />
              <Route path="/liveness/admin/api-tokens/new" element={<ApiTokenForm />} />
              <Route path="/liveness/admin/api-tokens/:id/edit" element={<ApiTokenForm />} />
              <Route path="/liveness/admin/accounts" element={<Accounts />} />
              <Route path="/liveness/admin/accounts/new" element={<AccountForm />} />
              <Route path="/liveness/admin/accounts/:id/edit" element={<AccountForm />} />
              <Route path="/liveness/admin/companies" element={<Companies />} />
              <Route path="/liveness/admin/companies/new" element={<CompanyForm />} />
              <Route path="/liveness/admin/companies/:id/edit" element={<CompanyForm />} />
              <Route path="/liveness/admin/settings" element={<AdminSettings />} />
              <Route path="/liveness/admin/ai" element={<AISettingsPage />} />
              <Route path="/liveness/admin/jobs" element={<Jobs />} />
              <Route path="/liveness/admin/global" element={<GlobalConfig />} />
            </Route>

            <Route path="/" element={<Navigate to="/pub/login" replace />} />
            <Route path="*" element={<Navigate to="/pub/login" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </I18nProvider>
    </div>
  );
}

export default App;
